import React from "react";
import { Drawer, Box, Stack, Typography, IconButton, Chip, Divider, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { PALETTE, ApprovalRow, statusTint, studentName, initials, objectiveLabel } from "../types";
import MasteryProgression from "./MasteryProgression";
import MasteryCriteriaSummary from "./MasteryCriteriaSummary";

const fmt = (iso?: string | null) => iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

type Props = {
  row: ApprovalRow | null;
  open: boolean;
  onClose: () => void;
  onApprove?: (r: ApprovalRow) => void;
  onReject?: (r: ApprovalRow) => void;
  canApprove?: boolean;
};

const MasteryApprovalDetailsDrawer: React.FC<Props> = ({ row, open, onClose, onApprove, onReject, canApprove }) => {
  if (!row) {
    return <Drawer anchor="right" open={false} onClose={onClose}><Box /></Drawer>;
  }
  const name = studentName(row);
  const tone = statusTint(row.status);
  const isPending = row.status === "pending";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100vw", sm: 440 }, display: "flex", flexDirection: "column" } }}
    >
      <Box sx={{ background: `linear-gradient(135deg, #0f1e3c 0%, ${PALETTE.navy} 60%, ${PALETTE.navy2} 100%)`, color: "#fff", p: 2.5 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1.25 }}>
          <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap" }}>
            <Chip size="small" label={row.status === "pending" ? "Pending Review" : row.status === "approved" ? "Approved" : "Rejected"} sx={{ bgcolor: "rgba(255,255,255,0.16)", color: "#fff", fontWeight: 800, fontSize: 10.5 }} />
            <Chip size="small" label="Supervisor Review" sx={{ bgcolor: "rgba(255,255,255,0.10)", color: "#e2e8f0", fontWeight: 700, fontSize: 10.5 }} />
          </Stack>
          <IconButton onClick={onClose} sx={{ color: "#fff", p: 0.5, ml: 1 }}><CloseIcon fontSize="small" /></IconButton>
        </Stack>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 44, height: 44, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.24)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 15 }}>{initials(name)}</Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.01em" }}>{name}</Typography>
            <Typography sx={{ fontSize: 12, opacity: 0.85 }}>{row.programName || "—"} · {row.targetName || `Target #${row.targetId}`}</Typography>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ p: 2.5, flex: 1, overflowY: "auto" }}>
        <Section title="Workflow">
          <Row label="Workflow" value={row.workflowName || "—"} />
          <Row label="Objective" value={objectiveLabel(row)} />
          <Row label="Current phase" value={row.phaseName || "—"} />
          <Row label="Requested outcome" value={row.requestedOutcome === "status" ? "Set target status" : "Advance to phase"} />
        </Section>

        <Divider sx={{ my: 2, borderColor: PALETTE.border }} />
        <SectionTitle>Proposed Progression</SectionTitle>
        <Box sx={{ p: 1.5, bgcolor: "#fafcff", border: `1px solid ${PALETTE.border}`, borderRadius: 1.5, mb: 1.5 }}>
          <MasteryProgression row={row} />
        </Box>

        <Divider sx={{ my: 2, borderColor: PALETTE.border }} />
        <MasteryCriteriaSummary criteria={row.criteria} />

        <Divider sx={{ my: 2, borderColor: PALETTE.border }} />
        <Section title="Student">
          <Row label="Name" value={name} />
          <Row label="Grade" value={row.studentGrade || "—"} />
        </Section>

        <Divider sx={{ my: 2, borderColor: PALETTE.border }} />
        <Section title="Audit">
          <Row label="Criteria met / raised" value={fmt(row.created_at)} />
          {row.requestedByName && <Row label="Raised by" value={row.requestedByName} />}
          {row.triggeredSessionId && <Row label="Triggered session" value={`#${row.triggeredSessionId}`} />}
          {!isPending && <Row label={row.status === "approved" ? "Approved by" : "Rejected by"} value={row.reviewedByName || "—"} />}
          {!isPending && <Row label="Decided at" value={fmt(row.reviewedAt)} />}
          {!isPending && row.reviewNotes && (
            <Box sx={{ p: 1.25, bgcolor: tone.bg, border: `1px solid ${tone.accent}`, borderRadius: 1.5, mt: 0.5 }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: tone.fg, mb: 0.5 }}>Note</Typography>
              <Typography sx={{ fontSize: 12.5, color: PALETTE.ink, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{row.reviewNotes}</Typography>
            </Box>
          )}
        </Section>
      </Box>

      {isPending && canApprove && (
        <Box sx={{ p: 2, borderTop: `1px solid ${PALETTE.border}`, bgcolor: "#fff" }}>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="outlined"
              startIcon={<CancelOutlinedIcon />}
              onClick={() => onReject?.(row)}
              sx={{ textTransform: "none", fontWeight: 800, color: PALETTE.rejected.fg, borderColor: PALETTE.rejected.accent, "&:hover": { bgcolor: PALETTE.rejected.bg, borderColor: PALETTE.rejected.fg } }}
            >Reject</Button>
            <Button
              variant="contained"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={() => onApprove?.(row)}
              sx={{ textTransform: "none", fontWeight: 800, bgcolor: PALETTE.approved.accent, color: "#fff", "&:hover": { bgcolor: PALETTE.approved.fg } }}
            >Approve</Button>
          </Stack>
        </Box>
      )}
    </Drawer>
  );
};

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: 10.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: PALETTE.slate, mb: 1 }}>{children}</Typography>
);
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box>
    <SectionTitle>{title}</SectionTitle>
    <Stack spacing={0.75}>{children}</Stack>
  </Box>
);
const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Stack direction="row" spacing={1.5} alignItems="baseline">
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: PALETTE.slate, minWidth: 130 }}>{label}</Typography>
    <Typography component="div" sx={{ fontSize: 13, fontWeight: 700, color: PALETTE.ink, flex: 1 }}>{value}</Typography>
  </Stack>
);

export default MasteryApprovalDetailsDrawer;
